import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  directoryApi,
  type OrganisationRoute,
  type ReplaceOrganisationRoutesRequest,
  type RouteCatalogue,
} from '../client';

/** Stable query-key factory for the static shipping-routes catalogue. */
export const routeCatalogueKeys = {
  all: ['directory', 'route-catalogue'] as const,
};

/** Per-org route-selection caches, scoped by `organisationId`. */
export const organisationRouteKeys = {
  all: ['directory', 'organisation-route'] as const,
  byOrganisation: (organisationId: string) =>
    ['directory', 'organisation-route', 'org', organisationId] as const,
};

/**
 * Fetch the static shipping-routes catalogue. Cached aggressively — the
 * catalogue is bundled JSON on the BE, so a 24h `staleTime` is fine.
 */
export function useRouteCatalogue(
  options?: Omit<UseQueryOptions<RouteCatalogue, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<RouteCatalogue, Error>({
    queryKey: routeCatalogueKeys.all,
    queryFn: () => directoryApi.getRouteCatalogue(),
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: Infinity,
    ...options,
  });
}

/**
 * Fetch every active route-selection row for an organisation. Pass `null`
 * for `organisationId` to keep the query disabled.
 */
export function useOrganisationRoutes(
  organisationId: string | null,
  options?: Omit<
    UseQueryOptions<OrganisationRoute[], Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) {
  return useQuery<OrganisationRoute[], Error>({
    queryKey: organisationId
      ? organisationRouteKeys.byOrganisation(organisationId)
      : organisationRouteKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getOrganisationRoutes(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Mutation around PUT `/organisationRoutes`. One call replaces the full
 * selection; the BE diffs and inserts / restores / soft-deletes as needed.
 */
/**
 * Star or un-star one route. The cached list is patched in place so the star
 * flips at once, then refetched.
 */
export function useSetOrganisationRoutePreferred() {
  const qc = useQueryClient();
  return useMutation<
    OrganisationRoute,
    Error,
    { organisationId: string; organisationRouteId: string; isPreferred: boolean }
  >({
    mutationKey: [...organisationRouteKeys.all, 'set-preferred'],
    mutationFn: ({ organisationRouteId, isPreferred }) =>
      directoryApi.setOrganisationRoutePreferred(organisationRouteId, isPreferred),
    onSuccess: (row, vars) => {
      qc.setQueryData<OrganisationRoute[]>(
        organisationRouteKeys.byOrganisation(vars.organisationId),
        (rows) => rows?.map((r) => (r.id === row.id ? row : r)),
      );
      qc.invalidateQueries({
        queryKey: organisationRouteKeys.byOrganisation(vars.organisationId),
      });
    },
  });
}

export function useReplaceOrganisationRoutes() {
  const qc = useQueryClient();
  return useMutation<OrganisationRoute[], Error, ReplaceOrganisationRoutesRequest>({
    mutationKey: [...organisationRouteKeys.all, 'replace'],
    mutationFn: (body) => directoryApi.replaceOrganisationRoutes(body),
    onSuccess: (_rows, vars) => {
      qc.invalidateQueries({
        queryKey: organisationRouteKeys.byOrganisation(vars.organisationId),
      });
    },
  });
}
